import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, cn, Dialog, Input, Switch, toast } from "@dl/web-core-js/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_SYNC_OPTIONS,
  PartColourGroupClient,
  ProductSyncClient,
  type SyncBlockerDTO,
  type SyncChangeDTO,
  type SyncCheckDTO,
  type SyncCreationDTO,
  type SyncOptions,
  type SyncReadiness,
  type SyncRequest,
  type SyncRunReportDTO,
  type SyncTargetDTO,
  type SyncTargetPlanDTO,
  type SyncTargetResultDTO,
} from "../../api/configurator";
import { refusalMessage } from "../../api/manufacturing";
import { OpenRecord } from "../../components/OpenRecord";
import { PartPicker } from "../manufacturing/PartPicker";
import { COLOURS_KEY } from "./PartColoursTab";

type T = (key: string, fallback: string, options?: Record<string, unknown>) => string;

/** A target's key in the dialog: its product, or the product colour it is missing. */
const keyOf = (target: { product_id: number | null; scheme_id: number | null }) =>
  target.product_id != null ? `p:${String(target.product_id)}` : `s:${String(target.scheme_id)}`;

/**
 * The product sync (docs/product-configurator/sync.md): from this product,
 * its other colours (same family and size) brought in line, or created when
 * missing. Pick the colours and what to sync, check (nothing is written: what
 * blocks, what would be created with its number, what each bill would
 * change), then sync. Every bill that changes gets a new revision stamped with
 * the sync's time.
 */
export function ProductSyncDialog({
  productId,
  preselect,
  onClose,
}: {
  productId: number;
  /** A target to tick on opening: a missing colour from the model grid. */
  preselect?: { scheme_id: number } | { product_id: number };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new ProductSyncClient(http), [http]);
  const [allColours, setAllColours] = useState(false);
  // Each missing colour is planned to say how near it is: the family's at once, every other on demand.
  const targets = useQuery({
    queryKey: [...COLOURS_KEY, "sync", productId, allColours],
    queryFn: () => client.targets(productId, allColours),
    placeholderData: keepPreviousData,
  });

  const [selected, setSelected] = useState<Set<string>>(() =>
    preselect ? new Set(["product_id" in preselect ? `p:${String(preselect.product_id)}` : `s:${String(preselect.scheme_id)}`]) : new Set(),
  );
  const [skus, setSkus] = useState<Record<string, string>>({});
  const [numbers, setNumbers] = useState<Record<string, Record<string, string>>>({});
  const [options, setOptions] = useState<SyncOptions>(DEFAULT_SYNC_OPTIONS);
  const [plan, setPlan] = useState<SyncCheckDTO | null>(null);
  // What the plan was made from: anything changed since, and it is checked again before a sync.
  const [planFor, setPlanFor] = useState("");
  const [report, setReport] = useState<SyncRunReportDTO | null>(null);

  const list = targets.data?.targets ?? [];
  const request = (only?: Set<string>): SyncRequest => ({
    product_id: productId,
    options,
    targets: list
      .filter((target) => (only ?? selected).has(keyOf(target)))
      .map((target) => {
        const key = keyOf(target);
        return target.product_id != null
          ? { product_id: target.product_id, numbers: numbers[key] }
          : { scheme_id: target.scheme_id, sku: (skus[key] ?? target.sku ?? "").trim(), numbers: numbers[key] };
      }),
  });
  const fingerprint = JSON.stringify(request());
  const stale = plan != null && planFor !== fingerprint;

  const fail = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
  const check = useMutation({
    mutationFn: () => client.check(request()),
    onSuccess: (checked) => {
      setPlan(checked);
      setPlanFor(fingerprint);
      setReport(null);
    },
    onError: fail,
  });
  // Only what the check found runnable: a blocked colour is left for its links.
  const runnable = new Set(
    (plan?.targets ?? [])
      .filter((target) => !blocked(target) && !target.in_sync)
      .map((target) => keyOf(target)),
  );
  const run = useMutation({
    mutationFn: () => client.run(request(runnable)),
    onSuccess: (done) => {
      setReport(done);
      setPlan(null);
      void queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
      void queryClient.invalidateQueries({ queryKey: ["product"] });
      const failed = done.targets.filter((target) => !target.done).length;
      if (failed > 0) toast.error(t("sync.run.failed", "{{n}} colours not synced: see why below.", { n: failed }));
      else toast.success(t("sync.run.done", "{{n}} colours synced", { n: done.targets.length }));
    },
    onError: fail,
  });

  const reference = targets.data?.reference;
  const existing = list.filter((target) => target.product_id != null);
  const missing = list.filter((target) => target.product_id == null);
  const toggle = (key: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title={t("sync.title", "Sync colours from {{sku}}", { sku: reference?.sku ?? "" })}
      description={
        reference
          ? t("sync.description", "{{name}}, {{colour}}, {{size}}: its other colours brought in line with it, or created from it.", {
              name: reference.name ?? reference.sku,
              colour: reference.colour ?? "",
              size: reference.size ?? t("model.one-size", "One size"),
            })
          : undefined
      }
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.close", "Close")}
          </Button>
          <Button variant="secondary" disabled={selected.size === 0 || check.isPending} loading={check.isPending} onClick={() => check.mutate()}>
            {plan && !stale ? t("sync.check-again", "Check again") : t("sync.check", "Check")}
          </Button>
          <Button
            variant="primary"
            disabled={!plan || stale || runnable.size === 0 || run.isPending}
            loading={run.isPending}
            onClick={() => run.mutate()}
          >
            {t("sync.run", "Sync {{n}} colours", { n: runnable.size })}
          </Button>
        </>
      }
    >
      {targets.isLoading && <p className="text-[13px] text-ink-muted">{t("table.loading", "Loading")}</p>}
      {reference?.problem && <p className="mb-3 text-[13px] text-risk">{reference.problem}</p>}
      {targets.data && (
        <div className="grid gap-4 lg:grid-cols-[minmax(18rem,26rem)_1fr]">
          <div className="flex min-w-0 flex-col gap-4">
            <section>
              <h3 className="mb-1 text-[13px] font-semibold">
                {t("sync.colours.existing", "Existing in {{size}}", { size: reference?.size ?? t("model.one-size", "One size") })}
              </h3>
              <ul className="divide-y divide-line rounded-md border border-line">
                {existing.map((target) => (
                  <TargetRow key={keyOf(target)} target={target} checked={selected.has(keyOf(target))} onToggle={() => toggle(keyOf(target))} />
                ))}
                {existing.length === 0 && (
                  <li className="px-3 py-2 text-[13px] text-ink-muted">{t("sync.colours.none", "The family has no other colour in this size.")}</li>
                )}
              </ul>
            </section>
            <h3 className="-mb-2 text-[13px] font-semibold">{t("sync.colours.to-create", "To create")}</h3>
            {missing.length === 0 && (
              <p className="text-[13px] text-ink-muted">
                {allColours
                  ? t("sync.colours.none-missing-all", "No product colour can be added to this model.")
                  : t("sync.colours.none-missing", "Every colour the family sells exists in this size.")}
              </p>
            )}
            {READINESS.map((readiness) => {
              const rows = missing.filter((target) => target.readiness === readiness);
              if (rows.length === 0) return null;
              return (
                <ReadinessGroup key={readiness} readiness={readiness} count={rows.length}>
                  {rows.map((target) => (
                    <TargetRow
                      key={keyOf(target)}
                      target={target}
                      checked={selected.has(keyOf(target))}
                      onToggle={() => toggle(keyOf(target))}
                      sku={skus[keyOf(target)] ?? target.sku ?? ""}
                      onSku={(value) => setSkus((current) => ({ ...current, [keyOf(target)]: value }))}
                    />
                  ))}
                </ReadinessGroup>
              );
            })}
            {(targets.data.others > 0 || allColours) && (
              <Switch
                checked={allColours}
                disabled={targets.isFetching}
                onChange={setAllColours}
                label={
                  allColours
                    ? t("sync.colours.all-on", "Every product colour, not only the family's")
                    : t("sync.colours.all", "Every product colour ({{n}} the family does not sell)", { n: targets.data.others })
                }
              />
            )}
            {targets.isFetching && allColours && <p className="text-[12px] text-ink-muted">{t("sync.colours.reading", "Reading every colour")}</p>}
            <OptionsPanel options={options} onChange={setOptions} />
          </div>
          <div className="min-w-0">
            {report ? (
              <RunReport report={report} />
            ) : plan ? (
              <>
                {stale && (
                  <p className="mb-2 rounded-md border border-warn bg-warn-soft px-3 py-2 text-[13px]">
                    {t("sync.stale", "Changed since the check: check again before syncing.")}
                  </p>
                )}
                <div className="flex max-h-[65vh] flex-col gap-3 overflow-auto pr-1">
                  {plan.targets.map((target) => (
                    <PlanCard
                      key={keyOf(target)}
                      plan={target}
                      numbers={numbers[keyOf(target)] ?? {}}
                      onNumber={(creation, value) =>
                        setNumbers((current) => ({ ...current, [keyOf(target)]: { ...(current[keyOf(target)] ?? {}), [creation]: value } }))
                      }
                      onLinked={() => check.mutate()}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="rounded-md border border-dashed border-line px-4 py-6 text-[13px] text-ink-muted">
                {t(
                  "sync.howto",
                  "Tick the colours, choose what to sync, then Check: nothing is written until Sync. The check says what blocks each colour, what would be created (with numbers you can change) and what each bill would change.",
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}

const READINESS: SyncReadiness[] = ["READY", "NEEDS_LINKS", "MISSING_MATERIALS", "NOT_SET"];

/** Missing colours by how near they are: the two that can be synced open, the others folded. */
function ReadinessGroup({ readiness, count, children }: { readiness: SyncReadiness; count: number; children: ReactNode }) {
  const { t } = useTranslation();
  const titles: Record<SyncReadiness, string> = {
    READY: t("sync.ready.ready", "Ready to create"),
    NEEDS_LINKS: t("sync.ready.needs-links", "Ready once sisters are linked"),
    MISSING_MATERIALS: t("sync.ready.missing", "Missing materials in the colour"),
    NOT_SET: t("sync.ready.not-set", "Part colours not set"),
  };
  const hints: Record<SyncReadiness, string> = {
    READY: t("sync.ready.ready.hint", "Every material exists in the colour: its pieces, semi-finished and versions are created."),
    NEEDS_LINKS: t("sync.ready.needs-links.hint", "Its materials exist; some are not linked to their sisters yet. The check offers each link."),
    MISSING_MATERIALS: t("sync.ready.missing.hint", "A panel, band or hardware piece does not exist in the colour. Add it (with its supplier), link it, then sync."),
    NOT_SET: t("sync.ready.not-set.hint", "Say which part colours it is made in on Engineering, Colours, Product colours."),
  };
  const open = readiness === "READY" || readiness === "NEEDS_LINKS";
  return (
    <details open={open} className="rounded-md border border-line">
      <summary className="cursor-pointer select-none px-3 py-1.5 text-[13px] font-semibold">
        {titles[readiness]} <span className="font-normal text-ink-muted">({count})</span>
      </summary>
      <p className="border-t border-line px-3 py-1 text-[12px] text-ink-faint">{hints[readiness]}</p>
      <ul className="divide-y divide-line border-t border-line">{children}</ul>
    </details>
  );
}

function blocked(plan: SyncTargetPlanDTO): boolean {
  return plan.problem != null || plan.blocking.length > 0 || plan.creations.some((creation) => creation.problem != null);
}

function TargetRow({
  target,
  checked,
  onToggle,
  sku,
  onSku,
}: {
  target: SyncTargetDTO;
  checked: boolean;
  onToggle: () => void;
  sku?: string;
  onSku?: (value: string) => void;
}) {
  const { t } = useTranslation();
  // A problem other than a SKU to type, or a material missing in the colour: nothing to sync yet.
  const disabled =
    (target.problem != null && (target.product_id != null || target.primary_code == null)) ||
    target.readiness === "MISSING_MATERIALS" ||
    target.readiness === "NOT_SET";
  return (
    <li className={cn("flex flex-wrap items-center gap-2 px-3 py-1.5 text-[13px]", disabled && "opacity-60")}>
      <input type="checkbox" className="size-4 accent-accent" checked={checked} disabled={disabled} onChange={onToggle}
        aria-label={target.colour ?? target.sku ?? ""} />
      <span className="min-w-0 flex-1 truncate font-medium">{target.colour}</span>
      {target.product_id != null ? (
        <>
          <Link to="/products/$productId" params={{ productId: String(target.product_id) }} className="font-mono text-accent hover:underline">
            {target.sku}
          </Link>
          {target.status && target.status !== "AVAILABLE" && <Badge size="sm" variant="neutral">{target.status.toLowerCase()}</Badge>}
        </>
      ) : (
        <>
          <Badge size="sm" variant="warning">{t("sync.colours.missing", "to create")}</Badge>
          <Input
            className="h-7 w-28 font-mono"
            value={sku ?? ""}
            placeholder={t("sync.colours.sku", "SKU")}
            aria-label={t("sync.colours.sku", "SKU")}
            onChange={(event) => onSku?.(event.target.value)}
          />
        </>
      )}
      {target.problem && <span className="w-full text-[12px] text-ink-muted">{target.problem}</span>}
      {target.readiness === "NEEDS_LINKS" && (
        <span className="w-full text-[12px] text-ink-muted">
          {t("sync.ready.links", "{{n}} sisters to link; the check offers each", { n: target.links })}
        </span>
      )}
      {target.missing.length > 0 && (
        <span className="w-full text-[12px] text-ink-muted">
          {t("sync.ready.needs", "Needs a {{code}} part like:", { code: target.primary_code ?? target.colour ?? "" })}
        </span>
      )}
      {target.missing.length > 0 && (
        <ul className="w-full list-disc pl-5 text-[12px] text-ink-muted">
          {target.missing.map((material) => (
            <li key={material} className="truncate">{material}</li>
          ))}
        </ul>
      )}
    </li>
  );
}

function OptionsPanel({ options, onChange }: { options: SyncOptions; onChange: (next: SyncOptions) => void }) {
  const { t } = useTranslation();
  const set = (key: keyof SyncOptions) => (value: boolean) => onChange({ ...options, [key]: value });
  const rows: Array<{ key: keyof SyncOptions; label: string; hint: string }> = [
    { key: "fabrication", label: t("sync.option.fabrication", "Fabrication BOM"), hint: t("sync.option.fabrication.hint", "The semi-finished (91-): its lines.") },
    { key: "made_parts", label: t("sync.option.made-parts", "Made parts"), hint: t("sync.option.made-parts.hint", "Each piece and bag: its bill, edges, sizes, drawing number.") },
    { key: "routing", label: t("sync.option.routing", "Routing"), hint: t("sync.option.routing.hint", "The steps, machines and programs of each bill synced. Off: each new revision keeps the target's own routing, and only lines are compared.") },
    { key: "versions", label: t("sync.option.versions", "Assembly versions"), hint: t("sync.option.versions.hint", "Each version's assembly BOM, boxes and packing, by code (CA, US).") },
    { key: "create_parts", label: t("sync.option.create-parts", "Create missing parts"), hint: t("sync.option.create-parts.hint", "Pieces, bags and versions with no counterpart in the colour.") },
    { key: "create_product", label: t("sync.option.create-product", "Create missing products"), hint: t("sync.option.create-product.hint", "A colour with no product: its record copied, colour swapped; no images, prices or inventory rules; out of Shopify.") },
    { key: "product_data", label: t("sync.option.product-data", "Product data"), hint: t("sync.option.product-data.hint", "Existing products: measurements, package sizes, assembly data.") },
    { key: "drawings", label: t("sync.option.drawings", "Drawings on new parts"), hint: t("sync.option.drawings.hint", "A created piece gets its source's current files (the same files, not copies).") },
    { key: "activate", label: t("sync.option.activate", "Activate the new revisions"), hint: t("sync.option.activate.hint", "Off: existing parts' new revisions wait as drafts. Created parts are always active.") },
  ];
  return (
    <section>
      <h3 className="mb-1 text-[13px] font-semibold">{t("sync.options", "What to sync")}</h3>
      <ul className="flex flex-col gap-1.5 rounded-md border border-line px-3 py-2">
        {rows.map((row) => (
          <li key={row.key}>
            <Switch checked={options[row.key]} onChange={set(row.key)} label={row.label} />
            <p className="ml-11 text-[12px] text-ink-faint">{row.hint}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function blockLabel(blocker: SyncBlockerDTO, t: T): string {
  switch (blocker.reason) {
    case "NO_SISTER":
      return t("sync.block.no-sister", "No {{colour}} sister: link one, or add the part in that colour.", { colour: blocker.target_code ?? "" });
    case "INACTIVE_SISTER":
      return t("sync.block.inactive", "Its {{colour}} sister is inactive: link an active one.", { colour: blocker.target_code ?? "" });
    case "NOT_LINKED":
      return t("sync.block.not-linked", "Not linked to its sisters: link its {{colour}} one.", { colour: blocker.target_code ?? "" });
    case "NO_COLOUR":
      return t("sync.block.no-colour", "Holds coloured parts but has no colour: give it its colour and sisters.");
    default:
      return t("sync.block.create-off", "Has to be created: turn on Create missing parts.");
  }
}

function creationLabel(kind: string, t: T): string {
  switch (kind) {
    case "PRODUCT":
      return t("sync.kind.product", "Product");
    case "SEMI_FINISHED":
      return t("sync.kind.semi-finished", "Semi-finished");
    case "VERSION":
      return t("sync.kind.version", "Version");
    case "PIECE":
      return t("sync.kind.piece", "Piece");
    default:
      return t("sync.kind.made", "Made part");
  }
}

function changeLabel(change: SyncChangeDTO, t: T): string {
  switch (change.kind) {
    case "BILL":
      return t("sync.change.bill", "New revision");
    case "PART":
      return t("sync.change.part", "Part data");
    case "BOXES":
      return t("sync.change.boxes", "Boxes");
    case "PACKING":
      return t("sync.change.packing", "Packing");
    case "PRODUCT_DATA":
      return t("sync.change.product-data", "Product data");
    default:
      return t("sync.change.version-missing", "Version not made");
  }
}

const quantity = (value: number | null) => (value == null ? "" : String(Number(value.toFixed(4))));

function PlanCard({
  plan,
  numbers,
  onNumber,
  onLinked,
}: {
  plan: SyncTargetPlanDTO;
  numbers: Record<string, string>;
  onNumber: (creation: string, value: string) => void;
  onLinked: () => void;
}) {
  const { t } = useTranslation();
  const isBlocked = blocked(plan);
  return (
    <section className="rounded-md border border-line">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <h4 className="text-[14px] font-semibold">{plan.colour}</h4>
        <span className="font-mono text-[12px] text-ink-muted">{plan.sku}</span>
        {plan.in_sync ? (
          <Badge size="sm" variant="success">{t("sync.state.in-sync", "In sync")}</Badge>
        ) : isBlocked ? (
          <Badge size="sm" variant="error">{t("sync.state.blocked", "Blocked")}</Badge>
        ) : (
          <Badge size="sm" variant="info">
            {t("sync.state.ready", "{{changes}} changes, {{creations}} to create", { changes: plan.changes.length, creations: plan.creations.length })}
          </Badge>
        )}
      </header>
      <div className="flex flex-col gap-3 px-3 py-2 text-[13px]">
        {plan.problem && <p className="text-risk">{plan.problem}</p>}
        {plan.blocking.length > 0 && <Blockers blockers={plan.blocking} onLinked={onLinked} />}
        {plan.creations.length > 0 && <Creations creations={plan.creations} numbers={numbers} onNumber={onNumber} />}
        {plan.changes.map((change) => (
          <ChangeRow key={`${change.kind}:${change.number}`} change={change} label={changeLabel(change, t)} />
        ))}
        {plan.notes.length > 0 && (
          <ul className="list-disc pl-5 text-[12px] text-ink-muted">
            {plan.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function Blockers({ blockers, onLinked }: { blockers: SyncBlockerDTO[]; onLinked: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const groups = useMemo(() => new PartColourGroupClient(http), [http]);
  const sync = useMemo(() => new ProductSyncClient(http), [http]);
  const [linking, setLinking] = useState<number | null>(null);
  const adopt = useMutation({
    mutationFn: (blocker: SyncBlockerDTO) => sync.adopt(blocker.part_id, blocker.suggestion!.part_id, blocker.target_colour_id!),
    onSuccess: () => {
      toast.success(t("sync.block.linked", "Linked"));
      onLinked();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  const link = useMutation({
    mutationFn: ({ part, sister }: { part: number; sister: number }) => groups.link(part, sister),
    onSuccess: () => {
      setLinking(null);
      toast.success(t("sync.block.linked", "Linked"));
      onLinked();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  return (
    <div>
      <h5 className="mb-1 text-[12px] font-semibold text-risk">{t("sync.blocking", "What blocks it")}</h5>
      <ul className="divide-y divide-line rounded-md border border-risk/40">
        {blockers.map((blocker) => (
          <li key={blocker.part_id} className="flex flex-col gap-1 px-2 py-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <OpenRecord target={{ kind: "part", id: blocker.part_id, tab: "colours" }} label={blocker.number} />
              <span className="font-mono">{blocker.number}</span>
              <span className="min-w-0 flex-1 truncate text-ink-muted">{blocker.name}</span>
              {blocker.used_in && <span className="text-[12px] text-ink-faint">{t("sync.block.in", "in {{part}}", { part: blocker.used_in })}</span>}
            </div>
            {blocker.suggestion && blocker.target_colour_id != null && (
              <div className="flex flex-wrap items-center gap-2 rounded-md bg-surface-sunk px-2 py-1">
                <span>
                  {blocker.suggestion.edge
                    ? t("sync.suggest.edge", "{{part}} uses {{number}} on that edge:", {
                        part: blocker.suggestion.seen_in,
                        number: blocker.suggestion.number,
                      })
                    : t("sync.suggest.line", "{{part}} uses {{number}} in its place:", {
                        part: blocker.suggestion.seen_in,
                        number: blocker.suggestion.number,
                      })}
                </span>
                <span className="min-w-0 truncate text-ink-muted">{blocker.suggestion.name}</span>
                <Button size="sm" variant="primary" loading={adopt.isPending && adopt.variables === blocker} disabled={adopt.isPending}
                  onClick={() => adopt.mutate(blocker)}>
                  {blocker.suggestion.needs_colour
                    ? t("sync.suggest.take-colour", "Make it {{colour}} and link it", { colour: blocker.target_code ?? "" })
                    : t("sync.suggest.take", "Link {{number}}", { number: blocker.suggestion.number })}
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span>{blockLabel(blocker, t)}</span>
              {blocker.reason !== "NO_COLOUR" && blocker.reason !== "CREATE_OFF" && linking !== blocker.part_id && (
                <Button size="sm" variant="secondary" onClick={() => setLinking(blocker.part_id)}>
                  {t("sync.block.link", "Link a sister")}
                </Button>
              )}
            </div>
            {linking === blocker.part_id && (
              <div className="max-w-md">
                <PartPicker
                  autoFocus
                  disabled={link.isPending}
                  placeholder={t("sync.block.pick", "Its {{colour}} part, by number or name", { colour: blocker.target_code ?? "" })}
                  exclude={[blocker.part_id]}
                  onPick={(picked) => link.mutate({ part: blocker.part_id, sister: picked.id })}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Creations({
  creations,
  numbers,
  onNumber,
}: {
  creations: SyncCreationDTO[];
  numbers: Record<string, string>;
  onNumber: (creation: string, value: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <h5 className="mb-1 text-[12px] font-semibold">{t("sync.creations", "To create")}</h5>
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-ink-muted">
            <th className="py-0.5 pr-2 font-medium">{t("sync.creations.kind", "What")}</th>
            <th className="py-0.5 pr-2 font-medium">{t("sync.creations.from", "From")}</th>
            <th className="py-0.5 pr-2 font-medium">{t("sync.creations.number", "Number")}</th>
            <th className="py-0.5 font-medium">{t("sync.creations.name", "Name")}</th>
          </tr>
        </thead>
        <tbody>
          {creations.map((creation) => (
            <tr key={creation.key} className="border-t border-line align-top">
              <td className="py-1 pr-2">{creationLabel(creation.kind, t)}</td>
              <td className="py-1 pr-2 font-mono">{creation.source_number}</td>
              <td className="py-1 pr-2">
                {creation.editable && creation.kind !== "PRODUCT" ? (
                  <Input
                    className="h-7 w-36 font-mono"
                    value={numbers[creation.key] ?? creation.number ?? ""}
                    aria-label={t("sync.creations.number", "Number")}
                    onChange={(event) => onNumber(creation.key, event.target.value)}
                  />
                ) : (
                  <span className="font-mono">{creation.number}</span>
                )}
                {creation.problem && <p className="mt-0.5 text-risk">{creation.problem}</p>}
              </td>
              <td className="py-1">{creation.name}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChangeRow({ change, label }: { change: SyncChangeDTO; label: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(change.lines.length > 0 && change.lines.length <= 6);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge size="sm" variant="neutral">{label}</Badge>
        {change.part_id != null && <OpenRecord target={{ kind: "part", id: change.part_id, tab: "bom" }} label={change.number} />}
        <span className="font-mono">{change.number}</span>
        {change.lines.length > 0 && (
          <button type="button" className="text-[12px] text-accent hover:underline" onClick={() => setOpen(!open)}>
            {open
              ? t("sync.change.hide", "Hide lines")
              : t("sync.change.lines", "{{n}} lines", { n: change.lines.length })}
          </button>
        )}
      </div>
      {change.details.map((detail) => (
        <p key={detail} className="ml-1 text-[12px] text-ink-muted">{detail}</p>
      ))}
      {open && change.lines.length > 0 && (
        <table className="mt-1 w-full text-[12px]">
          <tbody>
            {change.lines.map((line) => (
              <tr key={`${line.number}:${String(line.box)}`} className="border-t border-line">
                <td className={cn("py-0.5 pr-2 font-medium", line.change === "ADDED" ? "text-ok" : line.change === "REMOVED" ? "text-risk" : "text-warn")}>
                  {line.change === "ADDED"
                    ? t("sync.line.added", "added")
                    : line.change === "REMOVED"
                      ? t("sync.line.removed", "removed")
                      : t("sync.line.changed", "changed")}
                </td>
                <td className="py-0.5 pr-2 font-mono">{line.number}</td>
                <td className="max-w-64 truncate py-0.5 pr-2 text-ink-muted">{line.name}</td>
                <td className="py-0.5 pr-2 text-ink-muted">{line.box != null ? t("sync.line.box", "box {{n}}", { n: line.box }) : ""}</td>
                <td className="py-0.5 text-right tabular-nums">
                  {line.change === "CHANGED" ? `${quantity(line.from)} → ${quantity(line.to)}` : quantity(line.from ?? line.to)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RunReport({ report }: { report: SyncRunReportDTO }) {
  const { t } = useTranslation();
  return (
    <div className="flex max-h-[65vh] flex-col gap-3 overflow-auto pr-1">
      <p className="text-[13px] text-ink-muted">
        {t("sync.report.at", "Synced at {{at}}: every revision written is named so.", { at: report.at })}
      </p>
      {report.targets.map((target) => (
        <ResultCard key={`${String(target.product_id)}:${target.sku ?? ""}`} result={target} />
      ))}
    </div>
  );
}

function ResultCard({ result }: { result: SyncTargetResultDTO }) {
  const { t } = useTranslation();
  return (
    <section className="rounded-md border border-line px-3 py-2 text-[13px]">
      <header className="mb-1 flex flex-wrap items-center gap-2">
        <h4 className="text-[14px] font-semibold">{result.colour}</h4>
        {result.product_id != null ? (
          <Link to="/products/$productId" params={{ productId: String(result.product_id) }} className="font-mono text-accent hover:underline">
            {result.sku}
          </Link>
        ) : (
          <span className="font-mono">{result.sku}</span>
        )}
        <Badge size="sm" variant={result.done ? "success" : "error"}>
          {result.done ? t("sync.report.done", "Done") : t("sync.report.not-done", "Not synced")}
        </Badge>
      </header>
      {result.message && <p className={result.done ? "text-ink-muted" : "text-risk"}>{result.message}</p>}
      {result.created.length > 0 && (
        <p>
          <span className="font-medium">{t("sync.report.created", "Created")}: </span>
          {result.created.map((written) => written.number).join(", ")}
        </p>
      )}
      {result.revisions.length > 0 && (
        <p>
          <span className="font-medium">{t("sync.report.revisions", "New revisions")}: </span>
          {result.revisions
            .map((written) => `${written.number} (${written.kind === "ACTIVE" ? t("bom.status.active", "Active") : t("bom.status.draft", "Draft")})`)
            .join(", ")}
        </p>
      )}
      {result.updated.length > 0 && (
        <p>
          <span className="font-medium">{t("sync.report.updated", "Updated")}: </span>
          {result.updated.map((written) => written.number).join(", ")}
        </p>
      )}
      {result.to_do.length > 0 && (
        <>
          <p className="mt-1 font-medium">{t("sync.report.to-do", "Still to do")}</p>
          <ul className="list-disc pl-5 text-ink-muted">
            {result.to_do.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
